import { useDraggable } from '@dnd-kit/core'
import { uniqueId } from 'lodash'
import { CSSProperties, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Coordinates, Transform } from 'types'
import { iconComponent } from '../../types/icons'
import useDragCss from './hooks/use-drag-css'
import useTransform from './hooks/use-drag-transform'
import { Background, Container, Content, Ellipsis, Mover, Rotator, Scaler } from './style'

const DEFAULT_TRANSFORM = { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 }
const EllipsisIcon = iconComponent('ellipsis')

type Props = {
    children?: ReactNode
    className?: string
    defaultTransform?: Transform
    disabled?: boolean
    dndId?: string
    height: number
    hideBackground?: boolean
    ignoreAspectRatio?: boolean
    persistFocus?: boolean
    rotateable?: boolean
    scaleable?: boolean
    style?: CSSProperties
    width: number
    onContextMenu?: (e: SyntheticEvent<HTMLElement, MouseEvent>) => void
    onDoubleClick?: (e: SyntheticEvent<HTMLElement, MouseEvent>) => void
    onDragEnd?: (transform: Transform) => void
    onDragMove?: (transform: Transform) => void
    onDragStart?: (transform: Transform) => void
    onEllipsisMenu?: (e: SyntheticEvent<HTMLElement, MouseEvent>) => void
    onFocusChange?: (focused: boolean) => void
}

const Draggable = ({
    children,
    className,
    defaultTransform = DEFAULT_TRANSFORM,
    disabled = false,
    dndId,
    height,
    hideBackground = false,
    ignoreAspectRatio = false,
    persistFocus = false,
    rotateable = false,
    scaleable = false,
    style = {},
    width,
    onContextMenu,
    onDoubleClick,
    onDragEnd,
    onDragMove,
    onDragStart,
    onEllipsisMenu,
    onFocusChange,
}: Props) => {
    const idRef = useRef<string>(dndId || uniqueId('draggable'))
    const moverId = `${idRef.current}-mover`
    const rotatorId = `${idRef.current}-rotator`
    const scalerId = `${idRef.current}-scaler`

    const [{ x, y }, setTranslate] = useState<Coordinates>({ x: 0, y: 0 })
    const [{ x: scaleX, y: scaleY }, setScale] = useState<Coordinates>({ x: 0, y: 0 })
    const [rotation, setRotation] = useState<number>(0)

    const { isDragging: isMoving } = useDraggable({ id: moverId, disabled })
    const { isDragging: isRotating } = useDraggable({ id: rotatorId, disabled })
    const { isDragging: isScaling } = useDraggable({ id: scalerId, disabled })
    const isDragging = isMoving || isRotating || isScaling

    const position = useTransform(defaultTransform, { x, y, rotation, scaleX, scaleY })
    const center = useMemo(() => ({ x: width / 2, y: height / 2 }), [height, width])
    const dragStyle = useDragCss(position, width, height)
    const scaleAspectRatio = defaultTransform.scaleX / defaultTransform.scaleY
    const pointerEvents = disabled ? 'none' : undefined

    const onBlur = useCallback(() => onFocusChange?.(false), [onFocusChange])
    const onFocus = useCallback(() => onFocusChange?.(true), [onFocusChange])

    useEffect(() => {
        isDragging && onDragMove?.(position)
    }, [position])

    useEffect(() => {
        if (isDragging) {
            onDragStart?.(position)
        } else {
            onDragEnd?.(position)
            setTranslate({ x: 0, y: 0 })
            setScale({ x: 0, y: 0 })
            setRotation(0)
        }
    }, [isDragging])

    return (
        <Container
            className={className}
            style={{ ...dragStyle, ...style, pointerEvents }}
            tabIndex={0}
            onBlur={onBlur}
            onContextMenu={onContextMenu}
            onDoubleClick={onDoubleClick}
            onFocus={onFocus}
            data-dragging={persistFocus}
        >
            {!hideBackground && !disabled && <Background />}
            {children && <Content>{children}</Content>}
            {!disabled && (
                <>
                    <Mover dndId={moverId} onMove={setTranslate} />
                    {rotateable && <Rotator center={center} dndId={rotatorId} onRotate={setRotation} />}
                    {scaleable && (
                        <Scaler
                            aspectRatio={ignoreAspectRatio ? undefined : scaleAspectRatio}
                            center={center}
                            dndId={scalerId}
                            rotation={position.rotation}
                            onScale={setScale}
                        />
                    )}
                    {onEllipsisMenu && (
                        <Ellipsis onClick={onEllipsisMenu}>
                            <EllipsisIcon color="#fff" />
                        </Ellipsis>
                    )}
                </>
            )}
        </Container>
    )
}

export default Draggable
